import React from 'react';
import moment from 'moment';

interface PriorDiscussion {
  date_of_incident?: string;
  dateOfIncident?: string;
  date?: string;
  type: string;
  policy_violated?: string;
  policyViolation?:
    | {
        name?: string;
      }
    | string;
  policyViolations?: Array<{ name?: string } | string>;
  action_taken?: string;
  actionTaken?: string;
}

interface PriorDiscussionsTableProps {
  discussions: PriorDiscussion[];
}

const policyViolatedDisplay = (discussion: PriorDiscussion & Record<string, any>) => {
  if (discussion.policy_violated) {
    return discussion.policy_violated;
  }
  if (discussion.policyViolation?.name) {
    return discussion.policyViolation.name;
  }
  if (typeof discussion.policyViolation === 'string') {
    return discussion.policyViolation;
  }
  if (Array.isArray(discussion.policyViolations) && discussion.policyViolations.length > 0) {
    return discussion.policyViolations
      .map((pv: any) => (typeof pv === 'string' ? pv : pv?.name || ''))
      .filter(Boolean)
      .join(', ');
  }
  return '';
};

const actionTakenDisplay = (discussion: PriorDiscussion & Record<string, any>) => {
  const raw = (discussion.action_taken ?? discussion.actionTaken ?? '').trim();
  return raw || '—';
};

const formatPriorDiscussionDate = (discussion: PriorDiscussion & Record<string, any>) => {
  const raw = discussion.date_of_incident ?? discussion.dateOfIncident ?? discussion.date ?? '';
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return '';
  }
  const parsed = moment(raw);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : '';
};

const formatType = (type: string): string => {
  if (!type) return '';

  const typeMap: Record<string, string> = {
    improvement: 'Performance Improvement',
    verbal_warning: 'Verbal Warning',
    verbalwarning: 'Verbal Warning',
    disciplinary_action: 'Disciplinary Action',
    disciplinaryaction: 'Disciplinary Action',
    performanceoneonone: 'Performance One On One',
    performance_one_on_one: 'Performance One On One',
    feedback: 'Feedback',
  };

  const normalizedType = type.toLowerCase().replace(/_/g, '');
  if (typeMap[normalizedType]) {
    return typeMap[normalizedType];
  }

  return type
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const PriorDiscussionsTable = ({ discussions }: PriorDiscussionsTableProps) => {
  return (
    <div className="mb-4">
      <h5 className="text-primary mb-3">Prior Verbal Discussion or Written Warning</h5>
      <div
        style={{
          width: '100%',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          overflow: 'hidden',
          maxHeight: discussions.length > 5 ? '240px' : 'auto',
          overflowY: discussions.length > 5 ? 'auto' : 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
            <tr>
              <th
                style={{
                  width: '25%',
                  padding: '8px 12px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              >
                Date of Incident
              </th>
              <th
                style={{
                  width: '25%',
                  padding: '8px 12px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              >
                Type
              </th>
              <th
                style={{
                  width: '25%',
                  padding: '8px 12px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              >
                Policy Violation
              </th>
              <th
                style={{
                  width: '25%',
                  padding: '8px 12px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {discussions.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  style={{
                    padding: '16px',
                    textAlign: 'center',
                    color: '#6c757d',
                    fontSize: '0.9rem',
                  }}
                >
                  No prior discussions or warnings found
                </td>
              </tr>
            ) : (
              discussions.map((discussion: PriorDiscussion, index: number) => (
                <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '8px 12px', fontSize: '0.9rem', verticalAlign: 'middle' }}>
                    {formatPriorDiscussionDate(discussion)}
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontSize: '0.9rem',
                      verticalAlign: 'middle',
                    }}
                  >
                    {formatType(discussion.type)}
                  </td>
                  <td style={{ padding: '8px 12px', fontSize: '0.9rem', verticalAlign: 'middle' }}>
                    {policyViolatedDisplay(discussion)}
                  </td>
                  <td style={{ padding: '8px 12px', fontSize: '0.9rem', verticalAlign: 'middle' }}>
                    {actionTakenDisplay(discussion)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PriorDiscussionsTable;
